import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import Select from "react-select";
import { Loader } from "../../Common/Loader";
import AddFeesModal from "../../components/AddFeesModal";
// Icons
import { Calendar, Search, Users, DollarSign, CreditCard, Edit, Plus } from "lucide-react";
// API Helpers
import { getProgramList, getClassList, getYearlyFees, saveFees, updateFees, getAcademicYearList, getAcademicYearFees } from "helpers/backend_helper";
import { useClassStore } from "store/useClassStore";
import { useProgramStore } from "store/useProgramStore";
import { useAcademicYearStore } from "store/useAcademicYearStore";
import { useAuthStore } from "store/useAuthStore";

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };

// Month names for columns
const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

// Function to generate month headers based on AcademicYear dates
const generateMonthHeaders = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return [];
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const headers = [];
  
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  
  while (current <= endMonth) {
    const monthName = monthNames[current.getMonth()];
    const year = current.getFullYear();
    headers.push({
      month: current.getMonth() + 1,
      year: year,
      label: `${monthName} ${year}`,
      date: new Date(current)
    });
    
    current.setMonth(current.getMonth() + 1);
  }
  
  return headers;
};

// Calculate year options (current year and previous 5 years)
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 6; i++) {
    const year = currentYear - i;
    years.push({ value: year, label: year.toString() });
  }
  return years;
};

function FeesList() {

  const { 
    programs,
    selectedProgram, 
    setSelectedProgram,
    fetchPrograms
  } = useProgramStore();

  const { user } = useAuthStore();

  const { 
    classes,
    selectedClass, 
    setSelectedClass,
  } = useClassStore();

  const { 
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
  } = useAcademicYearStore();

  
  const [feesData, setFeesData] = useState<any[]>([]);
  const [filteredFees, setFilteredFees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');


  // API Data States
  const [yearlyFeesData, setYearlyFeesData] = useState<any[]>([]);
  const [isLoadingFees, setIsLoadingFees] = useState<boolean>(false);
  const [academicYearDetails, setAcademicYearDetails] = useState<any>(null);
  const [monthHeaders, setMonthHeaders] = useState<any[]>([]);

  // Use Zustand store data directly - convert to dropdown format
  const programOptions = useMemo(() => {
    const options = programs.map((program: any) => ({
      value: program.programId,
      label: program.programName
    }));
    return [defaultProgramOption, ...options];
  }, [programs]);

  const classOptions = useMemo(() => {
    const options = classes.map((classItem: any) => ({
      value: classItem.classId,
      label: classItem.className
    }));
    return [defaultClassOption, ...options];
  }, [classes]);

  // Convert selected values from Zustand store to dropdown format
  const selectedProgramOption = useMemo(() => {
    if (!selectedProgram) return defaultProgramOption;
    return {
      value: selectedProgram.programId,
      label: selectedProgram.programName
    };
  }, [selectedProgram]);

  const selectedClassOption = useMemo(() => {
    if (!selectedClass) return defaultClassOption;
    return {
      value: selectedClass.classId,
      label: selectedClass.className
    };
  }, [selectedClass]);

  // AcademicYear options
  const academicYearOptions = useMemo(() => {
    const options = academicYears.map((academicYear: any) => ({
      value: academicYear.academicYearId,
      label: academicYear.academicYearName
    }));
    return [...options];
  }, [academicYears]);

  const selectedAcademicYearOption = useMemo(() => {
    if (!selectedAcademicYear) return { value: "", label: "All Academic Years" };
    return {
      value: selectedAcademicYear.academicYearId,
      label: selectedAcademicYear.academicYearName
    };
  }, [selectedAcademicYear]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFeeData, setSelectedFeeData] = useState<any>(null);

  // Year options
  const yearOptions = getYearOptions();

  // Stats calculations
  const totalStudents = filteredFees.length;
  const totalCollected = filteredFees.reduce((total, student) => {
    let studentTotal = 0;
    student.monthlyFees?.forEach((monthFee: any) => {
      if (monthFee.feeId > 0 && monthFee.feesReceived > 0) {
        studentTotal += monthFee.feesReceived || 0;
      }
    });
    return total + studentTotal;
  }, 0);

  const totalPending = filteredFees.reduce((total, student) => {
    let pendingCount = 0;
    student.monthlyFees?.forEach((monthFee: any) => {
      if (monthFee.feeId === 0 || (monthFee.feeId > 0 && (monthFee.feesReceived === null || monthFee.feesReceived === 0))) {
        pendingCount++;
      }
    });
    return total + pendingCount;
  }, 0);

  const avgFeesPerStudent = totalStudents > 0 ? Math.round(totalCollected / totalStudents) : 0;

  const handleProgramChange = (option: any) => {
    // Option is already in the format {value: programId, label: programName}
    if (option && option.value && option.value !== "") {
      // Find the program from Zustand store and update it
      const program = programs.find(p => p.programId === option.value);
      if (program) {
        setSelectedProgram(program);
        console.log("Program selected:", program);
      }
    } else {
      setSelectedProgram(null);
      console.log("Program cleared");
    }
  };

  const handleClassChange = (option: any) => {
    // Option is already in the format {value: classId, label: className}
    if (option && option.value && option.value !== "") {
      // Find the class from Zustand store and update it
      const classItem = classes.find(c => c.classId === option.value);
      if (classItem) {
        setSelectedClass(classItem);
        console.log("Class selected:", classItem);
      }
    } else {
      setSelectedClass(null);
      console.log("Class cleared");
    }
  };

  const handleAcademicYearChange = (option: any) => {
    // Option is already in the format {value: academicYearId, label: academicYearName}
    if (option && option.value && option.value !== "") {
      // Find the academic year from Zustand store and update it
      const academicYear = academicYears.find(ay => ay.academicYearId === option.value);
      if (academicYear) {
        setSelectedAcademicYear(academicYear);
        console.log("Academic Year selected:", academicYear);
      }
    } else {
      setSelectedAcademicYear(null);
      console.log("Academic Year cleared");
    }
  };



  // Modal handlers
  const handleEditFees = (student: any, month: number, year?: number) => {
    const monthFee = student.monthlyFees?.find((fee: any) => fee.month === month && fee.year === year);
    const monthName = monthNames[month - 1];
    console.log("student:", student);
    setSelectedFeeData({
      studentId: student.studentId,
      studentName: student.studentName,
      guardianPhone: student.guardianPhone,
      guardianName: student.guardianName,
      month: month,
      monthName: monthName,
      year: year,
      monthYear: `${monthName} ${year}`,
      programName: selectedProgram?.programName,
      className: selectedClass?.className,
      existingData: monthFee ? {
        feeId: monthFee.feeId,
        totalFees: monthFee.totalFees,
        discount: monthFee.discount,
        feesReceived: monthFee.feesReceived,
        paymentMethodId: monthFee.paymentMethodId,
        notes: monthFee.notes
      } : null
    });

    console.log("Selected Fee Data:", selectedFeeData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeeData(null);
  };

  const handleSaveFees = async (feesData: any) => {
    try {
      // Create proper date format: YYYY-MM-01 (first day of the month)
      const monthStr = feesData.month.toString().padStart(2, '0');
      const feeMonthYear = `${feesData.year}-${monthStr}-01T00:00:00.000Z`;
      
      const payload = {
        clientId: 1,
        programId: selectedProgram?.programId,
        classId: selectedClass?.classId,
        studentId: feesData.studentId,
        month: feesData.month,
        year: feesData.year,
        feeMonthYear: feeMonthYear,
        totalFees: feesData.totalFees,
        discount: feesData.discount,
        feesReceived: feesData.feesReceived,
        paymentMethodId: feesData.paymentMethodId,
        notes: feesData.notes,
        createdBy: user?.userId // This should come from user context
      };

      let response;
      if (feesData.feeId && feesData.feeId > 0) {
        // Update existing fee
        response = await updateFees({
          ...payload,
          feeId: feesData.feeId
        });
      } else {
        // Create new fee
        response = await saveFees(payload);
      }

      console.log("Fees save response:", response);
      
      // Reload the fees data to reflect changes
      await loadYearlyFees();
      
    } catch (error) {
      console.error("Error saving fees:", error);
      throw error;
    }
  };


  // Load Yearly Fees from API
  const loadYearlyFees = async () => {
    if (!selectedProgram?.programId || !selectedClass?.classId || !academicYearDetails?.startDate || !academicYearDetails?.endDate) {
      setYearlyFeesData([]);
      setFilteredFees([]);
      return;
    }

    setIsLoadingFees(true);
    try {
      const response = await getAcademicYearFees({
        programId: selectedProgram?.programId,
        classId: selectedClass?.classId,
        clientId: 1, // Assuming clientId is 1, you might want to get this from user context
        startDate: academicYearDetails.startDate,
        endDate: academicYearDetails.endDate
      });
      
      console.log("Yearly fees response:", response);
      
      if (response.data && Array.isArray(response.data)) {
        setYearlyFeesData(response.data);
        setFeesData(response.data);
        setFilteredFees(response.data);
      } else {
        setYearlyFeesData([]);
        setFeesData([]);
        setFilteredFees([]);
      }
    } catch (error) {
      console.error("Error loading yearly fees:", error);
      setYearlyFeesData([]);
      setFeesData([]);
      setFilteredFees([]);
    } finally {
      setIsLoadingFees(false);
    }
  };

  // Load AcademicYear details and generate month headers
  useEffect(() => {
    const loadAcademicYearDetails = async () => {
      if (selectedAcademicYear?.academicYearId) {
        try {
          const response = await getAcademicYearList();
          const academicYear = response.data.find((ay: any) => ay.academicYearId === selectedAcademicYear.academicYearId);
          
          if (academicYear && academicYear.startDate && academicYear.endDate) {
            setAcademicYearDetails(academicYear);
            const headers = generateMonthHeaders(academicYear.startDate, academicYear.endDate);
            setMonthHeaders(headers);
            console.log("Generated month headers:", headers);
          }
        } catch (error) {
          console.error("Error loading AcademicYear details:", error);
        }
      } else {
        setAcademicYearDetails(null);
        setMonthHeaders([]);
      }
    };

    loadAcademicYearDetails();
  }, [selectedAcademicYear]);

  useEffect(() => {
    // Load data from Zustand stores if not already loaded
    const loadData = async () => {
      console.log("Starting to load dropdown data...");
      

      
      // Ensure default selections are set if not already selected
      if (programs.length > 0 && !selectedProgram) {
        setSelectedProgram(programs[0]);
        console.log("Set default program:", programs[0]);
      }
      
      if (classes.length > 0 && !selectedClass) {
        setSelectedClass(classes[0]);
        console.log("Set default class:", classes[0]);
      }
      
      setIsLoading(false);
      console.log("Finished loading dropdown data");
    };

    loadData();
  }, [programs.length, classes.length, selectedProgram, selectedClass, setSelectedProgram, setSelectedClass]);

  // Load fees data when filters change
  useEffect(() => {
      if (selectedProgram?.programId && selectedClass?.classId && academicYearDetails?.startDate && academicYearDetails?.endDate) {
      loadYearlyFees();
    } else {
      setFeesData([]);
      setFilteredFees([]);
    }
  }, [selectedProgram, selectedClass, academicYearDetails]);

  useEffect(() => {
    // Filter fees data based on search text
    const filtered = feesData.filter((student: any) => {
      const searchMatch = searchText === "" || 
        student.studentName.toLowerCase().includes(searchText.toLowerCase());
      return searchMatch;
    });
    setFilteredFees(filtered);
  }, [feesData, searchText]);

  // Debug effect to log Zustand store changes
  useEffect(() => {
    console.log("Program store updated:", { programs, selectedProgram });
  }, [programs, selectedProgram]);

  useEffect(() => {
    console.log("Class store updated:", { classes, selectedClass });
  }, [classes, selectedClass]);

  // Ensure default selections when stores are loaded
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      setSelectedProgram(programs[0]);
      console.log("Auto-selected first program:", programs[0]);
    }
  }, [programs, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
      console.log("Auto-selected first class:", classes[0]);
    }
  }, [classes, selectedClass, setSelectedClass]);

  // Generate columns for the table
  const columns = useMemo(() => {
    const cols = [
      {
        header: "Student ID",
        accessorKey: "studentId",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="font-medium text-slate-900 dark:text-zink-50">
            #{cell.getValue()}
          </div>
        ),
      },
      {
        header: "Student Name",
        accessorKey: "studentName",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="font-medium text-slate-900 dark:text-zink-50">
            {cell.getValue()}
          </div>
        ),
      }
    ];

    // Add columns for each month based on AcademicYear dates
    if (monthHeaders.length > 0) {
      monthHeaders.forEach((header) => {
        cols.push({
          header: header.label,
          accessorKey: `month_${header.month}_${header.year}`,
          enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const monthFee = row.monthlyFees?.find((fee: any) => fee.month === header.month && fee.year === header.year);
          
           if (monthFee && monthFee.feeId > 0 && monthFee.feesReceived > 0) {
             // Paid - show amount received
             return (
               <div className="text-center">
                 <div className="mb-1">
                   <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                     ${monthFee.feesReceived.toFixed(2)}
                   </span>
                 </div>
                 {monthFee.discount > 0 && (
                   <div className="text-xs text-gray-500 mb-1">
                     (Disc: ${monthFee.discount})
                   </div>
                 )}
                 {monthFee.totalFees && monthFee.feesReceived < (monthFee.totalFees - (monthFee.discount || 0)) && (
                   <div className="text-xs text-orange-600 mb-1">
                     Partial
                   </div>
                 )}
                 <button
                   onClick={() => handleEditFees(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                   title="Edit fees"
                 >
                   <Edit className="size-3 mr-1" />
                   Edit
                 </button>
               </div>
             );
           } else if (monthFee && monthFee.feeId > 0 && (monthFee.feesReceived === null || monthFee.feesReceived === 0)) {
             // Has fee record but not paid
             return (
               <div className="text-center">
                 <div className="mb-1">
                   <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                     Pending
                   </span>
                 </div>
                 <div className="text-xs text-gray-500 mb-1">
                   ${monthFee.totalFees?.toFixed(2)}
                 </div>
                 <button
                   onClick={() => handleEditFees(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                   title="Edit fees"
                 >
                   <Edit className="size-3 mr-1" />
                   Edit
                 </button>
               </div>
             );
           } else {
             // No fee record - show add button
             return (
               <div className="text-center">
                 <button
                   onClick={() => handleEditFees(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-green-600 hover:text-green-800 hover:bg-green-50 rounded dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20"
                   title="Add fees"
                 >
                   <Plus className="size-3 mr-1" />
                   Add
                 </button>
               </div>
             );
           }
        },
      });
      });
    } else {
      // Fallback to default month columns if no AcademicYear is selected
      monthNames.forEach((monthName, index) => {
        const monthNumber = index + 1;
        cols.push({
          header: monthName,
          accessorKey: `month_${monthNumber}`,
          enableColumnFilter: false,
          cell: (cell: any) => (
            <div className="text-center text-gray-400">
              No Academic Year selected
            </div>
          ),
        });
      });
    }

    return cols;
  }, [monthHeaders]);

  return (
    <React.Fragment>
     
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex flex-col gap-2 py-4 md:py-6">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2 xl:grid-cols-4">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-blue-100 rounded-md dark:bg-blue-500/20">
                      <Users className="size-6 text-blue-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalStudents}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Students</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-green-100 rounded-md dark:bg-green-500/20">
                      <DollarSign className="size-6 text-green-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">${totalCollected.toLocaleString()}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Collected</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-red-100 rounded-md dark:bg-red-500/20">
                      <CreditCard className="size-6 text-red-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalPending}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Pending Payments</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-yellow-100 rounded-md dark:bg-yellow-500/20">
                      <DollarSign className="size-6 text-yellow-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">${avgFeesPerStudent.toLocaleString()}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Avg per Student</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="card">
            <div className="card-body">
              
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Academic Year Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Academic Year"
                    name="academicYear"
                    options={academicYearOptions}
                    value={selectedAcademicYearOption}
                    onChange={handleAcademicYearChange}
                    isLoading={academicYears.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Program Filter */}
                <div className="lg:col-span-2">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={programOptions}
                    value={selectedProgramOption}
                    onChange={handleProgramChange}
                    isLoading={programs.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Class Filter */}
                <div className="lg:col-span-2">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={classOptions}
                    value={selectedClassOption}
                    onChange={handleClassChange}
                    isLoading={classes.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

              </div>

              {/* Fees Table */}
              {isLoading || isLoadingFees ? (
                <Loader />
              ) : !selectedProgram?.programId || !selectedClass?.classId || !selectedAcademicYear?.academicYearId ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <DollarSign className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">Select Filters</p>
                    <p>Please select an Academic Year, Program, and Class to view fees data.</p>
                  </div>
                </div>
              ) : filteredFees.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Users className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Students Found</p>
                    <p>No students found for the selected program, class, and year.</p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <TableContainer
                    columns={columns}
                    data={filteredFees}
                    isPagination={true}
                    divclassName="overflow-x-auto"
                    tableclassName="w-full whitespace-nowrap"
                    theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                    thclassName="px-3 py-2 font-semibold border-b border-slate-200 dark:border-zink-500 text-center"
                    tdclassName="px-3 py-2 border-y border-slate-200 dark:border-zink-500"
                    trclassName="even:bg-slate-50 dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  />
                </div>
              )}

            </div>
          </div>
         </div>
       </div>

       {/* Add Fees Modal */}
       {selectedFeeData && (
         <AddFeesModal
           isOpen={isModalOpen}
           onClose={handleCloseModal}
           onSave={handleSaveFees}
           programName={selectedFeeData.programName}
           className={selectedFeeData.className}
           studentName={selectedFeeData.studentName}
           studentId={selectedFeeData.studentId}
           monthYear={selectedFeeData.monthYear}
           month={selectedFeeData.month}
           year={selectedFeeData.year}
           phone={selectedFeeData.guardianPhone}
           guardianName={selectedFeeData.guardianName}
           existingData={selectedFeeData.existingData}
         />
       )}
     </React.Fragment>
   );
 }
 
 export default FeesList;
